import { useState, useCallback, useEffect } from 'react'
import { pwaApi, AnalyzeNoteResponse, GuardStatus } from '../api'
import type { ConversationState, ConversationActions } from './useConversation'
import type { Arrangement } from '../types/arrangement'
import { parseIncident, formatIncidentTitle, isIncident } from '../utils/incidentParser'
import { useOnline } from './useNetwork'
import { recordNote } from '../engines/offline_pipeline'
import { updateNote } from '../engines/offline_storage'
import { extractEntities } from '../engines/shared/entity_extractor'
import { classify } from '../engines/shared/classifier'

export interface E3Data {
  header: string                 // "Natalia, 07.06.26"
  body: string                   // treść notatki
  arrangements: string           // plain-text fallback (backward compat)
  arrangementList: Arrangement[] // lista ustaleń z datami i statusem
}

export interface NavigationActions {
  e3Data: E3Data
  setE3Body: (v: string) => void
  setE3Arrangements: (v: string) => void
  addE3Arrangement: (a: Arrangement) => void
  updateE3Arrangement: (id: string, fields: Partial<Omit<Arrangement, 'id'>>) => void
  removeE3Arrangement: (id: string) => void
  setE3ArrangementList: (list: Arrangement[]) => void

  // E1 → E2: po zakończeniu dyktowania. Waliduje, zapisuje do bazy, nawiguje.
  // `isHardwarePtt` = zgłoszenie wykryte z nagrania rozpoczętego fizycznym PTT
  // (gruszka/słuchawka/symulacja) — steruje Auto-Submit hands-free na E2b/E3b.
  goE1toE2: (fullText: string, name: string, analyzePromise?: Promise<AnalyzeNoteResponse>, isHardwarePtt?: boolean) => Promise<void>

  // E2 → E3: pobiera notatkę z bazy PRZED animacją, nawiguje gdy dane gotowe
  goE2toE3: () => Promise<void>

  // E3 → E2: zapisuje notatkę do bazy, wraca na E2
  goE3toE2: () => void

  // E3 → E1: finalny zapis, reset całego stanu, nawiguje na E1
  goE3toE1: (actionType: 'NOTE' | 'WARNING' | 'REPRIMAND', formData?: { imie: string; nazwisko: string; stanowisko: string }) => void

  // Reset sesji — wywoływany przez idle timer, tap mic, onCancel E2→E1
  resetSession: () => void
}

export function useNavigation(
  conv: ConversationState & ConversationActions,
  navigateTo: (screen: 1 | 2 | 3) => void,
  onResetSignal?: () => void
): NavigationActions {

  const [e3Data, setE3Data] = useState<E3Data>({ header: '', body: '', arrangements: '', arrangementList: [] })
  const isOnline = useOnline()

  const setE3Body             = useCallback((v: string) => setE3Data(p => ({ ...p, body: v })), [])
  const setE3Arrangements     = useCallback((v: string) => setE3Data(p => ({ ...p, arrangements: v })), [])
  const addE3Arrangement      = useCallback((a: Arrangement) => {
    console.log('[useNavigation] addE3Arrangement - adding:', a)
    console.log('[useNavigation] addE3Arrangement - current list:', e3Data.arrangementList.length, JSON.stringify(e3Data.arrangementList))
    setE3Data(p => {
      const newList = [...p.arrangementList, a]
      console.log('[useNavigation] addE3Arrangement - new list:', newList.length, JSON.stringify(newList))
      return { ...p, arrangementList: newList }
    })
  }, [e3Data.arrangementList])
  const updateE3Arrangement   = useCallback((id: string, fields: Partial<Omit<Arrangement, 'id'>>) => {
    console.log('[useNavigation] updateE3Arrangement - id:', id, 'fields:', JSON.stringify(fields))
    setE3Data(p => {
      const newList = p.arrangementList.map(a => a.id === id ? { ...a, ...fields } : a)
      console.log('[useNavigation] updateE3Arrangement - list after update:', newList.length, JSON.stringify(newList))
      return { ...p, arrangementList: newList }
    })
  }, [])
  const removeE3Arrangement   = useCallback((id: string) => {
    console.log('[useNavigation] removeE3Arrangement - id:', id)
    setE3Data(p => {
      const newList = p.arrangementList.filter(a => a.id !== id)
      console.log('[useNavigation] removeE3Arrangement - list after remove:', newList.length, JSON.stringify(newList))
      return { ...p, arrangementList: newList }
    })
  }, [])
  const setE3ArrangementList  = useCallback((list: Arrangement[]) => setE3Data(p => ({ ...p, arrangementList: list })), [])

  const buildNote = (d: E3Data) => {
    const plainText = d.arrangementList.length > 0
      ? d.arrangementList.map(a => {
          const datePart = a.date ? ` [do: ${a.date}]` : ''
          return `${a.text}${datePart}`
        }).join('\n')
      : d.arrangements
    return `${d.header}\n${d.body}${plainText ? '\n\nCo ustaliliście?\n' + plainText : ''}`
  }

  const formatHeader = (firstName: string, date: Date) => {
    const dd = String(date.getDate()).padStart(2, '0')
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const yy = String(date.getFullYear()).slice(2)
    const name = firstName && firstName !== 'PracownikX' ? firstName : 'Pracownik X'
    return `${name}, ${dd}.${mm}.${yy}`
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Reset sesji — idle timer E1, tap mic, onCancel E2→E1
  // ─────────────────────────────────────────────────────────────────────────
  const resetSession = useCallback(() => {
    conv.resetConversation()
    setE3Data({ header: '', body: '', arrangements: '', arrangementList: [] })
    onResetSignal?.()   // snap kafelek do pozycji domyślnej
    navigateTo(1)
  }, [conv, navigateTo, onResetSignal])

  // ─────────────────────────────────────────────────────────────────────────
  // E1 → E2
  // Warunki: fullText niepusty. name może być 'PracownikX' — dozwolone.
  // Działanie: pipeline async (zapis DB + conversationId), ustawia transcriptionText, nawiguje.
  // ─────────────────────────────────────────────────────────────────────────
  const goE1toE2 = useCallback(async (fullText: string, name: string, analyzePromise?: Promise<AnalyzeNoteResponse>, isHardwarePtt?: boolean) => {
    if (!fullText.trim()) return

    // Zapamiętaj źródło wywołania w współdzielonym stanie `conv` — bez tego
    // Auto-Submit hands-free na E2b/E3b nie wiedziałby, że nikt nie może
    // dotknąć ekranu (patrz `IncidentFormScreen`/`IncidentSummaryScreen`).
    conv.setIsHardwarePtt(!!isHardwarePtt)

    // ── Detektor słów kluczowych — WSPÓLNY dla wszystkich źródeł tekstu ─────────
    // `isIncident()` (ten sam, którego używa sprzętowy PTT w `Screen1.onDoneRef`
    // i pole „Mów lub napisz…” w `handleTypedNoteSubmit`) sprawdzamy PRZED każdą
    // inną heurystyką. Dzięki temu tekst-zgłoszenie trafia w formularz incydentu
    // tak samo, niezależnie czy przyszedł:
    //   • WPISANY z klawiatury (`handleTypedNoteSubmit`),
    //   • jako transkrypcja live z Deepgram (online, `useDictation` → `onDone`),
    //   • jako transkrypcja nagrania `MediaRecorder` zwrócona przez backend.
    // `parseIncident` wstępnie wypełnia pola z treści; czego nie wykryje, user
    // uzupełni ręcznie na E2. Spójne online/offline — nie zależy od `isOnline`
    // ani od lokalnego „fake-AI”, bo słowo kluczowe niesie pełną decyzję.
    if (isIncident(fullText)) {
      const kwForm = parseIncident(fullText)
      conv.setIsIncident(true)
      conv.setIncidentForm(kwForm)
      conv.setOriginalIncidentForm({ ...kwForm })
      conv.setTranscriptionText(fullText)
      navigateTo(2)
      return
    }

    // Wykrycie incydentu (ścieżka B) online — ten sam klasyfikator co offline
    // (encje + A_CONDUCT_WORDS/imię-na-starcie dla rozmowy vs słowniki zakładowe
    // dla awarii), zamiast osobnej, uboższej bramki słów-kluczowych. Ujednolica
    // zachowanie online/offline i korzysta z dużo lepiej dostrojonej heurystyki.
    if (isOnline) {
      const onlineEntities = await extractEntities(fullText, 'galvo', undefined, undefined)
      const onlineClassification = classify(onlineEntities, fullText)
      if (onlineClassification.path === 'B') {
        const onlineForm = parseIncident(fullText)
        conv.setIsIncident(true)
        conv.setIncidentForm(onlineForm)
        conv.setOriginalIncidentForm({ ...onlineForm })
        conv.setTranscriptionText(fullText)
        navigateTo(2)
        return
      }
    }

    setE3Data({ header: '', body: '', arrangements: '', arrangementList: [] })

    const safeName = name?.trim() || 'PracownikX'

    conv.setTranscriptionText(
      safeName !== 'PracownikX' ? `${safeName}: ${fullText}` : fullText
    )

    // OFFLINE: lokalny fake-AI + guard + słownik
    if (!isOnline) {
      const record = await recordNote({
        transcript: fullText,
        industry: 'galvo',
        managerId: undefined
      })

      conv.setGuardStatus((record.guardStatus as GuardStatus) ?? null)
      conv.setComplianceWarning(record.warningText || null)
      conv.setLastNoteId(record.id)

      if (record.guardStatus && record.guardStatus !== 'OK') {
        conv.setScriptBriefing(null)
        return
      }

      if (record.path === 'B') {
        const form = parseIncident(fullText)
        const { bold, rest } = formatIncidentTitle(form)
        record.incidentForm = form
        record.title = `${bold}${rest}`
        record.shortNote = `${form.lokalizacja || ''} | ${form.obiekt || ''} | ${form.typ || ''}`.replace(/^\s*\|\s*|\s*\|\s*$/g, '').replace(/^\|+\s*|\s*\|+$/g, '')
        await updateNote(record)
        conv.setIsIncident(true)
        conv.setIncidentForm(form)
        // Snapshot automatycznie wykrytej formy — do porównania z korektami użytkownika
        // na E2b/E3b (samouczenie się na podstawie poprawek).
        conv.setOriginalIncidentForm({ ...form })
        conv.setScriptBriefing(null)
      } else {
        conv.setIsIncident(false)
        conv.setIncidentForm(null)
        conv.setScriptBriefing(record.script)
      }

      navigateTo(2)
      return
    }

    const forceNewEmployee = !!analyzePromise
    const effectiveAnalyzePromise = analyzePromise ?? pwaApi.analyzeNote(fullText)

    conv.runDictationPipeline(fullText, safeName, () => {
      navigateTo(2)
    }, effectiveAnalyzePromise, forceNewEmployee, () => {})
  }, [conv, navigateTo, resetSession, isOnline])

  // ─────────────────────────────────────────────────────────────────────────
  // E2 → E3
  // LAZY LOADING: Pobiera notatkę i ustalenia z bazy - nie polegamy na stanie lokalnym
  // Ustalenia są zapisywane do bazy natychmiast na E2 (patchArrangements)
  // ─────────────────────────────────────────────────────────────────────────
  const goE2toE3 = useCallback(async () => {
    // Wyczyść natychmiast — zapobiega wyświetleniu ustaleń z poprzedniej rozmowy
    setE3Data({ header: '', body: '', arrangements: '', arrangementList: [] })

    let header = ''
    let body   = ''

    const convId = conv.conversationId
    // FIX: Nie wywołuj API dla tymczasowych ID (tmp_*) - jeszcze nie ma ich w bazie
    const isTempId = typeof convId === 'string' && convId.startsWith('tmp_')
    if (convId && !isTempId) {
      try {
        // LAZY LOADING: pobierz pełne dane z bazy w tym ustalenia (arrangements_json)
        const data = await pwaApi.getConversation(convId)
        const firstName = data.employeeFirstName || 'PracownikX'
        const date      = data.createdAt ? new Date(data.createdAt) : new Date()
        header = formatHeader(firstName, date)
        
        if (data.fullNote) {
          body = data.fullNote
        } else if (conv.transcriptionText) {
          const ci = conv.transcriptionText.indexOf(': ')
          body = ci > 0 ? conv.transcriptionText.slice(ci + 2) : conv.transcriptionText
        }
      } catch (err) {
        console.error('[useNavigation] goE2toE3 - error fetching conversation:', err)
      }
    }

    // Fallback: parsuj z transcriptionText
    if (!header && conv.transcriptionText) {
      const colonIdx = conv.transcriptionText.indexOf(': ')
      const emp  = colonIdx > 0 ? conv.transcriptionText.slice(0, colonIdx) : 'PracownikX'
      const note = colonIdx > 0 ? conv.transcriptionText.slice(colonIdx + 2) : conv.transcriptionText
      header = formatHeader(emp, new Date())
      body   = note
    }

    console.log('[useNavigation] goE2toE3 - using local arrangements:', e3Data.arrangementList.length)
    
    setE3Data({ 
      header, 
      body, 
      arrangements: '', 
      arrangementList: e3Data.arrangementList
    })
    navigateTo(3)
  }, [conv, e3Data, navigateTo])

  // Efekt do logowania zmian e3Data
  useEffect(() => {
    console.log('[useNavigation] e3Data.arrangementList changed:', e3Data.arrangementList.length, JSON.stringify(e3Data.arrangementList))
  }, [e3Data.arrangementList])

  // ─────────────────────────────────────────────────────────────────────────
  // E3 → E2
  // Zapisuje aktualną notatkę do bazy (PATCH), wraca na E2.
  // ─────────────────────────────────────────────────────────────────────────
  const goE3toE2 = useCallback(() => {
    const note = buildNote(e3Data)
    const convId = conv.conversationId
    // FIX: Nie wywołuj API dla tymczasowych ID (tmp_*)
    const isTempId = typeof convId === 'string' && convId.startsWith('tmp_')
    if (convId && note.trim() && !isTempId) {
      pwaApi.updateNote(convId, note).catch(() => {})
    }
    // Wyczyść ustalenia — notatka zapisana, nowe dyktowanie zacznie od zera
    setE3Data(p => ({ ...p, arrangements: '', arrangementList: [] }))
    navigateTo(2)
  }, [conv.conversationId, e3Data, navigateTo])

  // ─────────────────────────────────────────────────────────────────────────
  // E3 → E1
  // Wyzwalacze: zielony (NOTE), czerwony (WARNING/REPRIMAND).
  // Warunki: conversationId musi istnieć.
  // Działanie: PATCH finalny, finalizeAndSend, reset, nawiguj.
  // ─────────────────────────────────────────────────────────────────────────
  const goE3toE1 = useCallback((actionType: 'NOTE' | 'WARNING' | 'REPRIMAND', formData?: { imie: string; nazwisko: string; stanowisko: string }) => {
    console.log('[useNavigation] goE3toE1 - CALLED with actionType:', actionType)
    console.log('[useNavigation] goE3toE1 - e3Data.arrangementList BEFORE:', e3Data.arrangementList.length, JSON.stringify(e3Data.arrangementList))
    
    const note    = buildNote(e3Data)
    const convId  = conv.conversationId

    // Sanity check: jeśli brak conversationId, pipeline się nie ukończył - nie ma czego finalizować
    if (!convId) {
      console.error('[goE3toE1] Brak conversationId - pipeline E1→E2 się nie ukończył lub się wysypał')
    }

    // Dla WARNING/REPRIMAND: jeśli są dane z formularza, zaktualizuj employee przed finalizacją
    const updateEmployeeIfNeeded = () => {
      if (formData && convId) {
        const item = conv.conversationItems?.find(c => c.conversation_id === convId)
        const empId = item?.employee_id
        if (empId) {
          return pwaApi.updateEmployee(empId, {
            firstName: formData.imie || undefined,
            lastName:  formData.nazwisko || undefined,
            position:  formData.stanowisko || undefined,
          }).catch(() => {})
        }
      }
      return Promise.resolve()
    }

    // Zapamiętaj arrangements I body PRZED resetem e3Data (finalizeAndSend potrzebuje ich w tle)
    const arrangementsToSave    = e3Data.arrangements
    const arrangementListToSave = e3Data.arrangementList
    const existingNoteToSave    = e3Data.body || undefined
    
    console.log('[useNavigation] goE3toE1 - captured arrangementListToSave:', arrangementListToSave.length, JSON.stringify(arrangementListToSave))

    // Wyciągnij imię z nagłówka E3 (format: "Natalia, 07.06.26")
    const headerName = e3Data.header ? e3Data.header.split(',')[0].trim() : ''
    const nameParts  = headerName.split(' ')
    const firstName  = nameParts[0] || ''
    const lastName   = nameParts.slice(1).join(' ') || ''

    // Nawiguj natychmiast — finalizacja w tle
    navigateTo(1)
    setE3Data({ header: '', body: '', arrangements: '', arrangementList: [] })
    onResetSignal?.()

    // OPTIMISTIC INSERT — natychmiast wstaw placeholder z is_indexing=true
    // Nie czekamy na roundtrip do API — user widzi kafelek z imieniem i "indeksowanie…" od razu
    const optimisticId = convId ? `optimistic-${convId}-${Date.now()}` : undefined
    if (convId) {
      const optimisticItem = {
        conversation_id: convId as number,
        employee_id:     0,
        first_name:      firstName,
        last_name:       lastName,
        position:        null,
        short_summary:   null,
        long_summary:    null,
        arrangements_transcription: null,
        arrangements_json: arrangementListToSave || [],
        created_at:      new Date().toISOString(),
        is_indexing:     true,
        _optimistic:     true,
        optimisticId:    optimisticId,
      }
      conv.setConversationItems(prev => [optimisticItem, ...(prev ?? [])])
    }

    // FIX: Nie wywołuj API dla tymczasowych ID (tmp_*)
    const isTempId = typeof convId === 'string' && convId.startsWith('tmp_')
    // Finalizacja asynchronicznie (conv.conversationId jeszcze nie zresetowane — reset dopiero w onDone)
    const notePromise = (convId && note.trim() && !isTempId)
      ? pwaApi.updateNote(convId, note).catch(() => {})
      : Promise.resolve()

    notePromise
      .then(() => updateEmployeeIfNeeded())
      .finally(() => {
        conv.finalizeAndSend({
          actionType,
          arrangements: arrangementsToSave,
          arrangementList: arrangementListToSave,
          existingNote: existingNoteToSave,
          optimisticId,
          onClipboard: (text) => navigator.clipboard?.writeText(text).catch(() => {}),
          onDone: () => conv.resetConversation(),
        })
      })
  }, [conv, e3Data, navigateTo, onResetSignal])

  return { e3Data, setE3Body, setE3Arrangements, addE3Arrangement, updateE3Arrangement, removeE3Arrangement, setE3ArrangementList, goE1toE2, goE2toE3, goE3toE2, goE3toE1, resetSession }
}
